/* Loomish Abt us
 * Infinite vertical gallery with an image-to-content transition.
 *
 * Ported from the Codrops tutorial "Infinite Scroll Gallery with Page
 * Transitions using GSAP" (surya-aditya/codrops-infinite-scroll-and-content-transition).
 * The loop, parallax, scrub and transition choreography are the original's.
 *
 * What is Loomish's, and why:
 *   - The demo owns the whole document. A section cannot. Scroll is captured
 *     only while the gallery holds the viewport, and given back at the switch.
 *   - SplitText is a Club plugin. Replaced with a local splitter that returns
 *     the same { chars, lines } shape.
 *   - Distance travelled is counted, so the switch can appear on the lap.
 */
(function () {
  'use strict';

  var GSAP_BASE = 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/';
  var pending = {};

  /* ---------------------------------------------------------------- utils */

  /** Load a script once; repeat calls share the first promise. */
  function loadOnce(url) {
    if (pending[url]) return pending[url];
    pending[url] = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = url;
      s.async = true;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return pending[url];
  }

  /** gsap core is in theme.liquid; only Flip is fetched — the scroll observer is gone. */
  function ready() {
    if (!window.gsap) return Promise.reject(new Error('gsap missing'));
    var needed = [];
    if (!window.Flip) needed.push(loadOnce(GSAP_BASE + 'Flip.min.js'));
    return Promise.all(needed);
  }

  /**
   * Split elements into characters, grouped into visual lines.
   * Stands in for SplitText: same { chars, lines, revert } surface, except
   * lines are arrays of chars rather than wrapper elements — gsap tweens
   * an array just as happily.
   */
  function split(els) {
    var roots = [].concat(els).filter(Boolean);
    var originals = roots.map(function (el) {
      return { el: el, html: el.innerHTML };
    });
    var chars = [];

    roots.forEach(function (root) {
      var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
      var texts = [];
      var node;
      while ((node = walker.nextNode())) {
        if (node.nodeValue && node.nodeValue.trim()) texts.push(node);
      }

      texts.forEach(function (text) {
        var frag = document.createDocumentFragment();

        // Keep whitespace as text nodes so words still break naturally
        text.nodeValue.split(/(\s+)/).forEach(function (token) {
          if (!token) return;

          if (/^\s+$/.test(token)) {
            frag.appendChild(document.createTextNode(token));
            return;
          }

          var word = document.createElement('span');
          word.className = 'lmab-word';

          // Iterate by code point so accents and emoji stay whole
          Array.from(token).forEach(function (ch) {
            var c = document.createElement('span');
            c.className = 'char';
            c.textContent = ch;
            word.appendChild(c);
            chars.push(c);
          });

          frag.appendChild(word);
        });

        text.parentNode.replaceChild(frag, text);
      });
    });

    // Group by rendered top edge; the chars are already in document order
    var lines = [];
    var current = null;
    var prevTop = null;

    chars.forEach(function (c) {
      var top = Math.round(c.getBoundingClientRect().top);
      if (prevTop === null || Math.abs(top - prevTop) > 2) {
        current = [];
        lines.push(current);
        prevTop = top;
      }
      current.push(c);
    });

    return {
      chars: chars,
      lines: lines,
      revert: function () {
        originals.forEach(function (o) {
          o.el.innerHTML = o.html;
        });
      }
    };
  }

  /* ----------------------------------------------------------- verticalLoop */

  /** Animate items along the y-axis in a loop. Unchanged from the original. */
  function verticalLoop(items, config) {
    var gsap = window.gsap;
    items = gsap.utils.toArray(items);
    config = config || {};

    // Endless timeline
    var tl = gsap.timeline({
      repeat: config.repeat,
      paused: config.paused,
      defaults: { ease: 'none' },
      onReverseComplete: function () {
        // Keep the playhead in range when playing backwards past zero
        tl.totalTime(tl.rawTime() + tl.duration() * 100);
      }
    });

    var length = items.length;

    // Measure from the top so any top padding is travelled before a wrap
    var startY = 0;

    // Height of each item, in px
    var heights = [];

    // Starting y of each item, relative to its height
    var yPercents = [];

    // Travel speed: speed 1 = 100px per second
    var pixelsPerSecond = (config.speed || 1) * 100;

    // Round yPercent values so items land on whole pixels
    var snap = config.snap === false ? function (v) { return v; } : gsap.utils.snap(config.snap || 1);

    // Measure each item and convert its y into a height-relative yPercent
    gsap.set(items, {
      yPercent: function (i, el) {
        var h = (heights[i] = parseFloat(gsap.getProperty(el, 'height', 'px')));
        yPercents[i] = snap(
          (parseFloat(gsap.getProperty(el, 'y', 'px')) / h) * 100 + gsap.getProperty(el, 'yPercent')
        );
        return yPercents[i];
      }
    });
    gsap.set(items, { y: 0 });

    // Full container length, including the spacing at the wrap seam
    var totalHeight =
      items[length - 1].offsetTop +
      (yPercents[length - 1] / 100) * heights[length - 1] -
      startY +
      items[length - 1].offsetHeight * gsap.getProperty(items[length - 1], 'scaleY') +
      (parseFloat(config.paddingBottom) || 0);

    // Two tweens per item: exit top, then re-enter from the bottom
    for (var i = 0; i < length; i++) {
      var item = items[i];

      // Current y offset in px
      var curY = (yPercents[i] / 100) * heights[i];

      // Distance to the container's top edge
      var distanceToStart = item.offsetTop + curY - startY;

      // Distance until fully off-screen top
      var distanceToLoop = distanceToStart + heights[i] * gsap.getProperty(item, 'scaleY');

      tl.to(
        item,
        {
          yPercent: snap(((curY - distanceToLoop) / heights[i]) * 100),
          duration: distanceToLoop / pixelsPerSecond
        },
        0
      ).fromTo(
        // Then re-enter from the bottom end of the container
        item,
        {
          yPercent: snap(((curY - distanceToLoop + totalHeight) / heights[i]) * 100)
        },
        {
          yPercent: yPercents[i],
          duration: (totalHeight - distanceToLoop) / pixelsPerSecond,
          immediateRender: false
        },
        distanceToLoop / pixelsPerSecond
      );
    }

    // Pre-render every tween to avoid a first-frame jump
    tl.progress(1, true).progress(0, true);

    return tl;
  }

  /* ---------------------------------------------------------------- Reveal */

  /** Reveal each slide (image + caption) as it enters the stage. */
  function Reveal(root, opts) {
    var gsap = window.gsap;
    this.stagger = opts.stagger;
    this.items = new Map();

    gsap.utils.toArray(root.querySelectorAll('.lmab__slide')).forEach(function (slide) {
      var wrapper = slide.querySelector('.lmab__img-wrapper');
      var caption = slide.querySelector('figcaption');
      var chars = caption ? split(caption).chars : [];

      // Resting state: image and caption invisible
      gsap.set(wrapper, { autoAlpha: 0 });
      if (chars.length) gsap.set(chars, { autoAlpha: 0 });

      this.items.set(slide, { wrapper: wrapper, chars: chars });
    }, this);
  }

  /** Show slides that entered, top to bottom; reset the ones that left. */
  Reveal.prototype.toggle = function (changes, immediate) {
    var self = this;

    changes
      .filter(function (c) { return c.visible; })
      .sort(function (a, b) { return a.top - b.top; })
      .forEach(function (c, i) { self.show(c.el, i * self.stagger, immediate); });

    changes
      .filter(function (c) { return !c.visible; })
      .forEach(function (c) { self.hide(c.el); });
  };

  /** Fade the image in, then the caption character by character. */
  Reveal.prototype.show = function (slide, delay, immediate) {
    var gsap = window.gsap;
    var item = this.items.get(slide);
    if (!item) return;

    // The viewport moved around the slide, so there is no entrance to play
    if (immediate) {
      gsap.set([item.wrapper].concat(item.chars), { autoAlpha: 1, overwrite: true });
      return;
    }

    gsap.to(item.wrapper, {
      autoAlpha: 1,
      duration: 1,
      ease: 'power2.out',
      delay: delay,
      overwrite: true
    });

    if (item.chars.length) {
      gsap.to(item.chars, {
        autoAlpha: 1,
        duration: 0.4,
        ease: 'none',
        stagger: 0.025,
        delay: delay + 0.2,
        overwrite: true
      });
    }
  };

  /** Reset instantly so the reveal replays on the next entry. */
  Reveal.prototype.hide = function (slide) {
    var gsap = window.gsap;
    var item = this.items.get(slide);
    if (!item) return;

    gsap.set(item.wrapper, { autoAlpha: 0, overwrite: true });
    if (item.chars.length) gsap.set(item.chars, { autoAlpha: 0, overwrite: true });
  };

  /* ---------------------------------------------------------------- Slider */

  /** Infinite slider driven by wheel/touch scrolling. */
  function Slider(root, opts) {
    this.root = root;
    this.stage = root.querySelector('.lmab__stage');
    this.gallery = root.querySelector('.lmab__gallery');
    this.enabled = opts.enabled;
    this.onToggle = opts.onToggle;
    this.onTravel = opts.onTravel;
    this.scrubDuration = opts.scrubDuration;
    this.parallaxAmount = opts.parallaxAmount;

    this.createLoop();
    this.createParallax();
    this.createScrub();
    this.createObserver();

    this.resize();
  }

  /** Build the vertical loop from the gallery slides. */
  Slider.prototype.createLoop = function () {
    var gsap = window.gsap;

    // The gap between slides doubles as the loop's bottom padding
    var gap = parseFloat(getComputedStyle(this.gallery).rowGap) || 0;

    this.slides = gsap.utils.toArray(this.gallery.querySelectorAll('.lmab__slide'));

    // A paused one-lap timeline; its playhead is our scroll position
    this.loop = verticalLoop(this.slides, {
      repeat: -1,
      paused: true,
      paddingBottom: gap
    });

    // Wrapping the playhead past either end is what makes it endless
    this.wrap = gsap.utils.wrap(0, this.loop.duration());
  };

  /** Give each slide its own travel speed for a sense of depth. */
  Slider.prototype.createParallax = function () {
    // Speed multipliers: > 1 faster, < 1 slower
    var speeds = [1.3, 0.8, 1.15, 0.7, 1.25, 0.85];
    var amount = this.parallaxAmount;

    this.parallax = this.slides.map(function (slide, i) {
      return {
        el: slide,
        factor: (speeds[i % speeds.length] - 1) * amount,
        offset: 0,
        visible: false
      };
    });

    // Scatter the resting positions too
    this.applyParallax();
  };

  /** Offset each slide by its speed factor, report stage enters/leaves. */
  Slider.prototype.applyParallax = function (immediate) {
    var gsap = window.gsap;
    var changes = [];

    // The stage, not the window: the section need not be full height
    var host = this.stage.getBoundingClientRect();

    this.parallax.forEach(function (item) {
      var rect = item.el.getBoundingClientRect();

      // The slide's position without our offset
      var loopTop = rect.top - item.offset;

      // Offset by the speed factor; zero at the wrap point
      item.offset = item.factor * (loopTop + rect.height);
      gsap.set(item.el, { y: item.offset });

      // Check visibility from the final position
      var top = loopTop + item.offset;
      var visible = top < host.bottom && top + rect.height > host.top;

      // Collect the slides that entered or left
      if (visible !== item.visible) {
        item.visible = visible;
        changes.push({ el: item.el, visible: visible, top: top });
      }
    });

    if (changes.length && this.onToggle) this.onToggle(changes, immediate === true);
  };

  /** The eased playhead that smooths scroll input into loop time. */
  Slider.prototype.createScrub = function () {
    var gsap = window.gsap;
    var self = this;

    // Proxy object standing in for the scroll position
    this.playhead = { time: 0 };
    this.last = 0;

    // Eases the playhead toward the latest scroll target
    this.scrub = gsap.to(this.playhead, {
      time: 0,
      duration: this.scrubDuration,
      ease: 'power3.out',
      paused: true,
      onUpdate: function () {
        self.loop.time(self.wrap(self.playhead.time));
        self.applyParallax();

        // Distance actually covered, either direction, for the lap counter
        if (self.onTravel) {
          self.onTravel(Math.abs(self.playhead.time - self.last));
        }
        self.last = self.playhead.time;
      }
    });
  };

  /*
   * There is no observer any more, and no preventDefault anywhere.
   *
   * This used to take the wheel away from the page and drive the loop from raw
   * deltas. That is what made it feel like a trap: the scrollbar stopped
   * meaning anything, the back gesture fought it, and leaving needed a button
   * because there was no other way out.
   *
   * The gallery is now pinned inside a tall runway and its position is simply
   * a function of how far down that runway the page has scrolled. The browser
   * keeps its scroll; we only read it.
   */
  Slider.prototype.createObserver = function () {
    this.observer = null;
  };

  /** Move the loop to an absolute playhead time, eased. */
  Slider.prototype.seek = function (time) {
    this.scrub.vars.time = time;
    this.scrub.invalidate().restart();
  };

  /** Rebuild the loop when the viewport size settles. */
  Slider.prototype.resize = function () {
    var self = this;
    var id;

    this._onResize = function () {
      clearTimeout(id);
      id = setTimeout(function () { self.rebuild(); }, 200);
    };

    window.addEventListener('resize', this._onResize);
  };

  /** Move the scrub target by the scrolled distance. */
  Slider.prototype.scroll = function (o) {
    if (!this.enabled()) return;

    // Swipes (x or y) all drive the gallery
    var delta = Math.abs(o.deltaX) > Math.abs(o.deltaY) ? o.deltaX : o.deltaY;

    // Slides travel 100px per playhead second, so px / 100 gives time
    this.scrub.vars.time += delta / 100;
    this.scrub.invalidate().restart();
  };

  /** Freeze the inertia in place so a transition can take over. */
  Slider.prototype.freeze = function () {
    this.scrub.pause();

    // Sync the target to where we stopped, so the next scroll ramps from here
    this.scrub.vars.time = this.playhead.time;
    this.scrub.invalidate();
  };

  /** Hand the gallery over to a transition. */
  Slider.prototype.stop = function () { this.freeze(); };

  /** Take the gallery back once a transition has finished with it. */
  Slider.prototype.start = function () { this.scrub.play(); };

  /** Release the timeline, the observer and the listener. */
  Slider.prototype.destroy = function () {
    var gsap = window.gsap;

    this.scrub.kill();
    this.loop.kill();
    gsap.killTweensOf(this.slides);
    window.removeEventListener('resize', this._onResize);
  };

  /** Re-measure and rebuild the loop, preserving the current position. */
  Slider.prototype.rebuild = function () {
    var gsap = window.gsap;

    // 0 to 1 progress
    var progress = this.loop.progress();

    // Drop the old timeline and its transforms; the stagger lives in the CSS
    this.freeze();
    this.loop.kill();
    gsap.set(this.slides, { clearProps: 'transform' });

    // Re-measure the new viewport size and restore the position
    this.createLoop();
    this.loop.progress(progress, true);

    // Re-measured rather than travelled, so the reveals snap, not replay
    this.parallax.forEach(function (item) { item.offset = 0; });
    this.applyParallax(true);

    // Re-sync the scrub to the rebuilt loop's timing
    this.playhead.time = this.loop.time();
    this.last = this.playhead.time;
    this.scrub.vars.time = this.playhead.time;
    this.scrub.invalidate();
  };

  /* ------------------------------------------------------------ Transition */

  /** Image-to-content transition. */
  function Transition(root, opts) {
    var gsap = window.gsap;

    // Called once the gallery is free again
    this.onClose = opts.onClose;
    this.root = root;

    // Fixed overlay holding the preview image and the content column
    this.content = root.querySelector('.lmab__content');

    // Figure the clicked image morphs into
    this.preview = root.querySelector('.lmab__preview');
    this.previewImg = this.preview.querySelector('img');

    // One content group per slide, matched by data-index
    this.groups = gsap.utils.toArray(root.querySelectorAll('.lmab__group'));
    this.slides = gsap.utils.toArray(root.querySelectorAll('.lmab__slide'));
    this.back = root.querySelector('.lmab__back');
    this.scroller = root.querySelector('.lmab__group-list');

    this.activeSlide = null;
    this.tl = null;
    this.split = null;

    // Transition state = closed | opening | open | closing
    this.state = 'closed';

    // The preview's Flip identity
    this.preview.dataset.flipId = 'preview';
  }

  /** Animate to preview. */
  Transition.prototype.open = function (slide, index) {
    var gsap = window.gsap;
    var Flip = window.Flip;
    var self = this;

    if (this.state !== 'closed') return;
    this.state = 'opening';
    this.activeSlide = slide;

    this.fillContent(slide, index).then(function () {
      // A close() landing while the preview decoded has already reset us
      if (self.state !== 'opening') return;

      var parts = self.parts();
      parts.wrapper.dataset.flipId = 'preview';

      // Capture the thumbnail's bounds before the layout changes
      var state = Flip.getState(parts.wrapper);

      // Show the detail layout and hide the thumbnail, killing its reveal tween
      gsap.set(self.content, { display: 'block' });
      self.content.setAttribute('aria-hidden', 'false');
      if (self.scroller) self.scroller.scrollTop = 0;
      gsap.killTweensOf(parts.wrapper);
      gsap.set(parts.wrapper, { autoAlpha: 0 });

      // Split the text into lines and characters
      var active = self.groups.filter(function (g) { return g.classList.contains('active'); })[0];
      var targets = [self.back].concat(active ? Array.prototype.slice.call(active.children) : []);
      self.split = split(targets);

      self.tl = gsap
        .timeline({
          onComplete: function () {
            self.state = 'open';
            if (self.back) self.back.focus({ preventScroll: true });
          },

          // Fires when a cancelled open finishes rewinding
          onReverseComplete: function () { self.reset(); }
        })

        // Fade out the other slides and the caption
        .to(parts.others, { autoAlpha: 0, duration: 0.5, ease: 'power2.out' }, 0)
        .to(parts.caption, { autoAlpha: 0, duration: 0.3, ease: 'power2.out' }, 0)

        // Morph the selected image
        .add(
          Flip.from(state, {
            targets: self.preview,
            duration: 1.2,
            ease: 'power4.inOut',
            absolute: true
          }),
          0
        )
        .to(self.previewImg, { scale: 1, duration: 1.2, ease: 'power4.inOut' }, 0);

      // Fade the text in character by character, one line after another
      self.split.lines.forEach(function (line, i) {
        self.tl.fromTo(
          line,
          { autoAlpha: 0 },
          {
            autoAlpha: 1,
            duration: 1,
            ease: 'power3.out',
            stagger: 0.01
          },
          // Preview delay + line index * stagger delay
          0.8 + i * 0.06
        );
      });
    });
  };

  /** Animate the preview back into its slide. */
  Transition.prototype.close = function () {
    var gsap = window.gsap;
    var Flip = window.Flip;
    var self = this;

    // Close mid-animation
    if (this.state === 'opening') {
      this.state = 'closing';

      // Still waiting on the decode, so there is no timeline to rewind
      if (!this.tl) {
        this.reset();
        return;
      }

      this.tl.reverse();
      return;
    }

    if (this.state !== 'open') return;
    this.state = 'closing';

    var parts = this.parts();
    this.content.setAttribute('aria-hidden', 'true');

    this.tl = gsap
      .timeline({ onComplete: function () { self.reset(); } })

      // Fade the text out line by line
      .to(this.split.lines, { autoAlpha: 0, duration: 0.4, stagger: 0.04, ease: 'power1.out' }, 0)

      // Morph the preview back into the thumbnail's bounds
      .add(
        Flip.fit(this.preview, parts.wrapper, {
          duration: 1,
          ease: 'power3.inOut',
          absolute: true
        }),
        0
      )
      .to(this.previewImg, { scale: 1.2, duration: 1, ease: 'power3.inOut' }, 0)

      // Fade the other slides and caption back in
      .to(parts.others, { autoAlpha: 1, duration: 0.5, ease: 'power2.out' }, 0.5)
      .to(parts.caption, { autoAlpha: 1, duration: 0.4, ease: 'power2.out' }, 0.6);
  };

  /** Copy the clicked image into the preview and activate its content group. */
  Transition.prototype.fillContent = function (slide, index) {
    var img = slide.querySelector('.lmab__img');

    this.groups.forEach(function (group) {
      group.classList.toggle('active', Number(group.dataset.index) === index);
    });

    if (!img) return Promise.resolve();

    var thumb = img.currentSrc || img.src;

    // The thumbnail, laid in behind: if the full file is slow, the morph still
    // carries a picture rather than a hole
    this.preview.style.backgroundImage = 'url("' + thumb + '")';

    // The preview is far larger than the card, so it takes the larger file
    this.previewImg.removeAttribute('srcset');
    this.previewImg.removeAttribute('sizes');
    this.previewImg.src = img.dataset.full || thumb;
    this.previewImg.alt = img.alt;

    // Undecoded, the preview paints blank for the first frames of the morph
    return this.previewImg.decode().catch(function () {
      // Rejects if the src is swapped mid-flight; the transition still runs
    });
  };

  /** Collect the pieces of the active slide the animations need. */
  Transition.prototype.parts = function () {
    var slide = this.activeSlide;

    return {
      // Flip target
      wrapper: slide.querySelector('.lmab__img-wrapper'),

      // Title under the image
      caption: slide.querySelector('figcaption'),

      // Other slide items
      others: this.slides.filter(function (s) { return s !== slide; })
    };
  };

  /** Restore the DOM. */
  Transition.prototype.reset = function () {
    var gsap = window.gsap;
    var parts = this.parts();

    // The thumbnail only carries the Flip id while its slide is expanded
    delete parts.wrapper.dataset.flipId;

    if (this.split) this.split.revert();
    this.split = null;

    // Hide the overlay and clear style the animations left
    gsap.set(this.content, { display: 'none' });
    this.content.setAttribute('aria-hidden', 'true');
    gsap.set(this.preview, { clearProps: 'all' });
    this.preview.style.backgroundImage = '';
    gsap.set(this.previewImg, { clearProps: 'all' });
    gsap.set(parts.wrapper, { clearProps: 'all' });

    this.activeSlide = null;
    this.tl = null;
    this.state = 'closed';

    if (this.onClose) this.onClose();
  };

  /* ------------------------------------------------------------- the house */

  /**
   * Holds the section together: decides when the gallery owns scroll, counts
   * the distance travelled, and offers the way out once a lap is done.
   */
  function Gallery(root) {
    var self = this;
    var d = root.dataset;

    this.root = root;

    /*
     * Registered before anything below can fail. A half-built gallery still
     * owns listeners and a timeline, and if it is never registered nothing
     * can ever take them back - the editor would stack a fresh set on every
     * re-render until the section fought itself.
     */
    root.__lmab = this;
    live.push(this);
    this.stage = root.querySelector('.lmab__stage');
    this.rail = root.querySelector('.lmab__rail');
    this.railBtn = root.querySelector('.lmab__switch');

    this.editor = d.editor === '1';
    this.always = d.always === '1';

    // Reduced motion means the page keeps its scroll, always
    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.unlocked = this.always;

    this.transition = new Transition(root, {
      /*
       * Opening a card stops the loop. Closing one has to start it again —
       * unconditionally. This used to be gated on a flag from the days when
       * the section took the scroll, and that flag is now never true, so
       * closing a card left the gallery frozen for the rest of the page.
       */
      onClose: function () {
        if (self.slider) self.slider.start();
      }
    });

    /*
     * Reduced motion gets the cards as a plain column: no loop, no parallax,
     * no reveal, and the page keeps its own scroll. Opening a card still
     * works, because the writing must stay reachable.
     */
    if (this.reduce) {
      this.bindSlides();
      this.bindChrome();
      this.watch();
      root.classList.add('is-ready');
      return;
    }

    this.reveal = new Reveal(root, {
      // Milliseconds in the editor, seconds to gsap
      stagger: (d.stagger === undefined ? 120 : parseFloat(d.stagger)) / 1000
    });

    this.slider = new Slider(root, {
      enabled: function () { return self.transition.state === 'closed'; },
      onToggle: function (changes, immediate) { self.reveal.toggle(changes, immediate); },

      // 100% is the original's .75s glide; the setting scales that reference
      scrubDuration: 0.75 * ((parseFloat(d.scrub) || 100) / 100),
      parallaxAmount: (d.parallax === undefined ? 1 : parseFloat(d.parallax) / 100)
    });

    this.bindSlides();
    this.bindChrome();
    this.watch();

    root.classList.add('is-ready');

    // Nothing is visible until the first measure, whether or not we engage
    this.slider.applyParallax(true);
  }

  /** Clicking a slide opens it. */
  Gallery.prototype.bindSlides = function () {
    var self = this;

    this.transition.slides.forEach(function (slide, index) {
      slide.setAttribute('role', 'button');

      var open = function () {
        // Nothing captures the scroll any more, so a click is only ever a
        // click. There is no "first click gets you back in" state to check.
        if (self.transition.state !== 'closed') return;

        if (self.slider) self.slider.stop();
        self.transition.open(slide, index);
      };

      slide.addEventListener('click', open);

      slide.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      });
    });

    this.syncTabIndex();
  };

  /**
   * Every card is a real button in the tab order. They used to be reachable
   * only while the gallery held the scroll; it never holds it now, so hiding
   * them from the keyboard would just make the writing unreachable.
   */
  Gallery.prototype.syncTabIndex = function () {
    this.transition.slides.forEach(function (slide) {
      slide.setAttribute('tabindex', '0');
    });
  };

  /** The switch, the back button, Escape, and the click that lets you back in. */
  Gallery.prototype.bindChrome = function () {
    var self = this;

    if (this.railBtn) {
      this.railBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        if (!self.unlocked) return;
        self.release(true);
      });
    }

    if (this.transition.back) {
      this.transition.back.addEventListener('click', function (e) {
        e.stopPropagation();
        self.transition.close();
      });
    }

    /*
     * Swipe down to close, which is the gesture a phone expects from anything
     * that opened over the top of something else. Only counts when the column
     * is already at its top, so it cannot fight scrolling the writing.
     */
    var overlay = this.transition.content;
    var scroller = this.transition.scroller;

    if (overlay) {
      var startY = 0;
      var armed = false;

      overlay.addEventListener('touchstart', function (e) {
        if (self.transition.state !== 'open') { armed = false; return; }
        armed = !scroller || scroller.scrollTop <= 0;
        startY = e.changedTouches[0].clientY;
      }, { passive: true });

      overlay.addEventListener('touchend', function (e) {
        if (!armed) return;
        armed = false;
        if (e.changedTouches[0].clientY - startY > 90) self.transition.close();
      }, { passive: true });
    }

    /*
     * Escape closes an open piece. With nothing open, and only while the
     * section is actually the thing on screen, it is the way out — the same
     * move the exit button makes.
     *
     * There is deliberately no keydown handler swallowing arrows, space or
     * page keys any more. The page scrolls normally now, so keyboard scrolling
     * has to keep working; blocking it was only ever needed while the wheel
     * was being taken away.
     */
    this._onEscape = function (e) {
      if (e.key !== 'Escape') return;

      if (self.transition.state === 'open' || self.transition.state === 'opening') {
        self.transition.close();
        return;
      }

      if (self.pinned()) self.release();
    };
    document.addEventListener('keydown', this._onEscape);
  };

  /** Is the runway currently holding the screen. */
  Gallery.prototype.pinned = function () {
    if (!this.runway) return false;
    var r = this.runway.getBoundingClientRect();
    return r.top <= 0 && r.bottom > window.innerHeight;
  };

  /** Engage when the gallery has all but filled the viewport. */
  /*
   * THE RUNWAY.
   *
   * The gallery is pinned inside a tall wrapper and its position is a pure
   * function of how far the page has scrolled through that wrapper. Nothing is
   * hijacked: the scrollbar still means what it says, the back gesture works,
   * a trackpad flick works, and leaving is simply scrolling past the end.
   *
   * That also makes "two cycles" a real, measurable distance rather than a
   * running total of wheel deltas — the runway IS the two cycles.
   */
  Gallery.prototype.watch = function () {
    var self = this;

    this.runway = this.root.closest('[data-lmab-runway]') || this.root.parentNode;
    this.cycles = parseFloat(this.root.dataset.cycles) || 2;

    /*
     * How much of the runway the cycles occupy. The rest is the tail: the
     * gallery has come to rest, the rail is full, and the exit is standing.
     */
    this.loopFrac = parseFloat(this.root.dataset.loopFrac) || 0.85;
    if (this.loopFrac <= 0 || this.loopFrac > 1) this.loopFrac = 0.85;

    if (this.reduce || !this.slider) {
      this.root.classList.add('is-free', 'is-unlocked');
      this.reveal && this.showAll();
      return;
    }

    this.tick = false;
    this._onScroll = function () {
      if (self.tick) return;
      self.tick = true;
      requestAnimationFrame(function () { self.tick = false; self.advance(); });
    };

    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onScroll);
    this.advance();
  };

  /**
   * When the gallery will never take the scroll - the Theme Editor, or the
   * capture setting turned off - it used to just sit there frozen: cards
   * stopped wherever the loop first placed them, most of them still at zero
   * opacity because nothing had scrolled them into view. It read as broken,
   * and edits looked like they had not applied.
   *
   * So: show every card at once, and let the page's own scroll drive the
   * loop. The field drifts as you move down the page instead of holding it.
   */
  /**
   * Where are we down the runway, and where does the loop belong.
   *
   * The runway is two things end to end: the cycles, then the tail. The loop
   * is mapped onto the cycles alone, so it finishes its last pass with the
   * tail still to go — and that is the moment the exit appears. The rail is
   * driven by the same number, so watching the last hairline fill IS watching
   * the way out arrive. Nothing has to be timed against anything else.
   *
   * The playhead is eased by the scrub (power3.out), so arriving at the end
   * of the cycles glides to a stop rather than stopping dead.
   */
  Gallery.prototype.advance = function () {
    if (!this.slider || !this.runway) return;

    var r = this.runway.getBoundingClientRect();
    var travel = r.height - window.innerHeight;
    if (travel <= 0) return;

    // 0 as the pin takes hold, 1 as it lets go
    var p = Math.min(1, Math.max(0, -r.top / travel));

    // 0 at the first frame of the first cycle, 1 as the last cycle lands
    var q = Math.min(1, p / this.loopFrac);

    this.slider.seek(q * this.cycles * this.slider.loop.duration());
    this.setProgress(q);

    // 0.999, not 1: p is a ratio of two measured pixel heights, so the exact
    // boundary lands a hair under and the exit would wait a pixel too long.
    var out = this.always || q >= 0.999;
    if (out !== this.unlocked) {
      this.unlocked = out;
      this.root.classList.toggle('is-unlocked', out);
    }
  };
  /** Reduced motion, or no loop: show every piece at rest. */
  Gallery.prototype.showAll = function () {
    if (!this.slider || !this.reveal) return;
    var all = this.slider.parallax.map(function (item) {
      item.visible = true;
      return { el: item.el, visible: true, top: 0 };
    });
    this.reveal.toggle(all, true);
  };

  /** Take the scroll. */
  /** The way out is a scroll, not a lock to undo. */
  Gallery.prototype.release = function () {
    if (!this.runway) return;
    var r = this.runway.getBoundingClientRect();
    var to = window.scrollY + r.bottom - window.innerHeight + 2;
    window.scrollTo({ top: to, behavior: this.reduce ? 'auto' : 'smooth' });
  };

  /**
   * Give everything back. The Theme Editor re-renders this section on every
   * keystroke in a text field; without this, each render left behind a live
   * timeline, an observer and three window listeners all still driving DOM
   * that had already been thrown away. That is what "the text will not
   * change" looks like from the outside.
   */
  Gallery.prototype.destroy = function () {
    if (this.io) this.io.disconnect();
    if (this._onScroll) {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onScroll);
    }
    if (this._onEscape) document.removeEventListener('keydown', this._onEscape);

    if (this.transition && this.transition.split) {
      this.transition.split.revert();
      this.transition.split = null;
    }

    if (this.slider) this.slider.destroy();
  };

  /** One number drives the rail: how much of the cycles are behind us. */
  Gallery.prototype.setProgress = function (q) {
    this.root.style.setProperty('--lmab-lap', String(q));
    if (this.rail) this.rail.setAttribute('aria-valuenow', String(Math.round(q * 100)));
  };

  /* ------------------------------------------------------------------ boot */

  /**
   * Wait for the pictures, but not for long.
   *
   * The loop's measurements do not depend on them: every card wrapper carries
   * its own width and aspect-ratio, and the image is absolutely positioned
   * inside it. So this is only about not revealing empty frames — which is
   * worth about a second of patience and no more.
   */
  function whenImages(root, limit) {
    var imgs = Array.prototype.slice.call(root.querySelectorAll('.lmab__img'));

    var all = Promise.all(
      imgs.map(function (img) {
        if (img.complete) return Promise.resolve();
        return new Promise(function (res) {
          img.addEventListener('load', res, { once: true });
          img.addEventListener('error', res, { once: true });
        });
      })
    );

    var cutoff = new Promise(function (res) { setTimeout(res, limit); });

    return Promise.race([all, cutoff]);
  }

  /**
   * Hold until the section is actually on screen.
   * The reveal is an entrance. Run it at load and the visitor arrives to a
   * gallery that has already finished introducing itself.
   */
  function whenSeen(root) {
    return new Promise(function (resolve) {
      // In the editor the section may sit below the preview's fold while the
      // author is editing it. Waiting on visibility there means waiting for
      // a scroll that never comes, and the section stays at zero opacity.
      if (root.dataset.editor === '1' || !('IntersectionObserver' in window)) {
        resolve();
        return;
      }

      var io = new IntersectionObserver(
        function (entries) {
          if (entries[0].intersectionRatio < 0.15) return;
          io.disconnect();
          resolve();
        },
        { threshold: [0, 0.15] }
      );

      io.observe(root);
    });
  }

  function mount(root) {
    if (root.dataset.mounted === '1') return;
    root.dataset.mounted = '1';

    // Seen first, then the pictures: lazy images do not start until the
    // section is near, so waiting on them beforehand waits on nothing
    whenSeen(root)
      .then(function () { return whenImages(root, 1200); })
      .then(function () {
        // Torn down while we waited
        if (!document.body.contains(root)) return;

        root.classList.remove('is-loading');
        try {
          // Registers itself; see the constructor
          new Gallery(root);
        } catch (e) {
          // A broken gallery must never take the page's scroll with it
          root.classList.add('is-free', 'is-unlocked');
          if (window.console) console.error('[loomish-abt-us]', e);
        }
      });
  }

  /*
   * Every live gallery, so orphans can be found. The editor swaps a section's
   * markup out from under us, and the element we were driving is detached
   * before any event tells us so - scoped cleanup alone would miss it.
   */
  var live = [];

  function sweep(scope) {
    for (var i = live.length - 1; i >= 0; i--) {
      var g = live[i];
      var detached = !document.body.contains(g.root);
      var inScope = scope && scope.contains && scope.contains(g.root);

      if (!detached && !inScope) continue;

      try { g.destroy(); } catch (e) {}
      g.root.__lmab = null;
      g.root.removeAttribute('data-mounted');
      live.splice(i, 1);
    }
  }

  function boot() {
    var roots = document.querySelectorAll('[data-lmab]:not([data-mounted])');
    if (!roots.length) return;

    ready().then(function () {
      roots.forEach(mount);
    }).catch(function () {
      // No gsap, no gallery — leave the static markup readable and scrollable
      roots.forEach(function (r) { r.classList.remove('is-loading'); r.classList.add('is-free', 'is-unlocked'); });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /*
   * Theme Editor re-renders the section without a page load. The old markup
   * is replaced, so the old instance must be dismantled before the new one
   * is built - otherwise two loops drive one gallery and neither wins.
   */
  document.addEventListener('shopify:section:load', function (e) {
    sweep(e.target);
    boot();
  });

  document.addEventListener('shopify:section:unload', function (e) {
    sweep(e.target);
  });
})();
